import React, { FC, CSSProperties, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dropdownStyle } from '../components/StyledSelect';
import { useAuth } from "../contexts/AuthContext";
import { apiUrl } from "../utils/api";
import DateTimePickerModal from '../components/DateTimePickerModal';

// --- Type Definitions ---

interface DrillingData {
    datetime: string;
    shift: string;
    machine: string;
    mine: string;
    location: string;
    well: string;
    startDepth: string;
    endDepth: string;
    drilled: string;
    type: string;
}

// --- API Types ---

export interface IdName {
    id: string;
    id_int?: string;
    name: string;
}

export interface ContractorRef extends IdName {}
export interface DepositRef extends IdName {}
export interface MineRef extends IdName { deposit_id?: string; }
export interface InclineRef extends IdName { mine_id?: string; }

export interface StationRef extends IdName {
    status?: string;
    well_id?: string;
    contractor_id?: string;
    self_equipment_id?: string;
}

export interface EquipmentRef {
    id: string;
    name: string;
    type_name?: string;
    serial_number?: string;
    gamma?: string;
    meters_drilled?: string;
    hours_drilled?: string;
}

export interface WorkShiftRef {
    id: string;
    shift_number: number;
    shift_start: string;
    shift_end: string;
}

export interface DrillerRef {
    id: string;
    name: string;
    is_working?: boolean;
}

export interface WellRef extends IdName {
    status?: string;
    current_depth?: string;
    incline_id?: string;
    is_archived?: boolean;
}

export interface WorkKindRef {
    id: string;
    name: string;
    category_id?: string;
}

export interface WorkCategoryRef {
    id: string;
    name: string;
    npt_type?: string;
}

export interface ReportParamsResponse {
    code: number;
    message: string;
    main_refs: {
        contractor_list: ContractorRef[];
        deposit_list: DepositRef[];
        mine_list: MineRef[];
        incline_list: InclineRef[];
        station_list: StationRef[];
    };
    equipment: {
        telemetry_system_list: EquipmentRef[];
        pdm_list: EquipmentRef[];
        drill_bit_list: EquipmentRef[];
    };
    crew: {
        work_shift_list: WorkShiftRef[];
        driller_list: DrillerRef[];
    };
    well_list: WellRef[];
    work_kind_list: WorkKindRef[];
    work_category_list: WorkCategoryRef[];
}

export interface ReportPayload {
    report_date: string;
    main_refs: {
        contractor_id: string;
        deposit_id: string;
        mine_id: string;
        incline_id: string;
        station_id: string;
    };
    equipment: {
        telemetry_system_id: string;
        pdm_id: string;
        drill_bit_id: string;
    };
    crew: {
        shift_id: string;
        driller_id_list: string[];
    };
    wells_stats_list: Array<{
        id: string;
        stats_by_time_list: Array<{
            work_kind_id: string;
            time_start: number;
            time_end: number;
        }>;
        stats_by_depth_list: Array<{
            work_category_id: string;
            depth_start: number;
            depth_end: number;
        }>;
    }>;
}

export interface ReportMutationResponse {
    code: number;
    message: string;
    report_id?: string;
    id?: string;
}

export interface ReportViewResponse {
    code: number;
    message: string;
    report: {
        main_refs: {
            contractor?: ContractorRef;
            deposit?: DepositRef;
            mine?: MineRef;
            incline?: InclineRef;
            station?: StationRef;
        };
        equipment: {
            telemetry_system?: EquipmentRef;
            pdm?: EquipmentRef;
            drill_bit?: EquipmentRef;
        };
        crew: {
            work_shift?: WorkShiftRef;
            driller_list: DrillerRef[];
        };
        well_stats: {
            well?: WellRef;
            stats_by_time_list: Array<{
                work_kind?: WorkKindRef;
                time_start: number;
                time_end: number;
            }>;
            stats_by_depth_list: Array<{
                work_category?: WorkCategoryRef;
                depth_start: string | number;
                depth_end: string | number;
            }>;
        };
        report_date: string;
    };
}

// --- Report API clients ---

const resolveToken = (token?: string): string => {
    if (token) return token;
    if (typeof localStorage === 'undefined') return '';
    return localStorage.getItem('auth_token') || localStorage.getItem('access_token') || '';
};

const getApiHeaders = (token?: string, jsonBody = false): HeadersInit => {
    const authToken = resolveToken(token);
    return {
        'Accept': 'application/json',
        ...(jsonBody ? { 'Content-Type': 'application/json' } : {}),
        ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
    };
};

const readJson = async <T,>(response: Response): Promise<T> => {
    const text = await response.text();
    return text ? JSON.parse(text) as T : ({} as T);
};

const ensureOk = async (response: Response, action: string): Promise<void> => {
    if (!response.ok) {
        let message = `${action} failed: ${response.status}`;
        try {
            const data = await response.clone().json();
            if (data?.message) message += ` — ${data.message}`;
        } catch {
            // ignore non-json error body
        }
        throw new Error(message);
    }
};

export const fetchReportParams = async (token?: string): Promise<ReportParamsResponse> => {
    const response = await fetch(apiUrl('/statistics/reports/params'), {
        method: 'GET',
        headers: getApiHeaders(token),
    });
    await ensureOk(response, 'report params fetch');
    return readJson<ReportParamsResponse>(response);
};

export const createReport = async (payload: ReportPayload, token?: string): Promise<ReportMutationResponse> => {
    const response = await fetch(apiUrl('/statistics/reports'), {
        method: 'POST',
        headers: getApiHeaders(token, true),
        body: JSON.stringify(payload),
    });
    await ensureOk(response, 'report create');
    return readJson<ReportMutationResponse>(response);
};

export const getReport = async (reportId: string, token?: string): Promise<ReportViewResponse> => {
    const response = await fetch(apiUrl(`/statistics/reports/${encodeURIComponent(reportId)}`), {
        method: 'GET',
        headers: getApiHeaders(token),
    });
    await ensureOk(response, 'report fetch');
    return readJson<ReportViewResponse>(response);
};

export const updateReport = async (reportId: string, payload: ReportPayload, token?: string): Promise<ReportMutationResponse> => {
    const response = await fetch(apiUrl(`/statistics/reports/${encodeURIComponent(reportId)}`), {
        method: 'PATCH',
        headers: getApiHeaders(token, true),
        body: JSON.stringify(payload),
    });
    await ensureOk(response, 'report update');
    return readJson<ReportMutationResponse>(response);
};

// --- Mock Data ---

const mockData: DrillingData[] = [
    {
        datetime: '12.03.2023 13:31',
        shift: '2',
        machine: 'Станок 1',
        mine: '«Есаульская»',
        location: 'Заезд на КШ 26-29',
        well: '101',
        startDepth: '0',
        endDepth: '254',
        drilled: '254',
        type: 'ВЗД'
    },
    // ... (repeating data omitted for brevity)
    {
        datetime: '12.03.2023 13:31',
        shift: '2',
        machine: 'Станок 1',
        mine: '«Есаульская»',
        location: 'Заезд на КШ 26-29',
        well: '101',
        startDepth: '0',
        endDepth: '254',
        drilled: '254',
        type: 'ВЗД'
    },
    {
        datetime: '12.03.2023 13:31',
        shift: '2',
        machine: 'Станок 1',
        mine: '«Есаульская»',
        location: 'Заезд на КШ 26-29',
        well: '101',
        startDepth: '0',
        endDepth: '254',
        drilled: '254',
        type: 'ВЗД'
    },
    {
        datetime: '12.03.2023 13:31',
        shift: '2',
        machine: 'Станок 1',
        mine: '«Есаульская»',
        location: 'Заезд на КШ 26-29',
        well: '101',
        startDepth: '0',
        endDepth: '254',
        drilled: '254',
        type: 'ВЗД'
    },
    {
        datetime: '12.03.2023 13:31',
        shift: '2',
        machine: 'Станок 1',
        mine: '«Есаульская»',
        location: 'Заезд на КШ 26-29',
        well: '101',
        startDepth: '0',
        endDepth: '254',
        drilled: '254',
        type: 'ВЗД'
    },
    {
        datetime: '12.03.2023 13:31',
        shift: '2',
        machine: 'Станок 1',
        mine: '«Есаульская»',
        location: 'Заезд на КШ 26-29',
        well: '101',
        startDepth: '0',
        endDepth: '254',
        drilled: '254',
        type: 'ВЗД'
    },
    {
        datetime: '12.03.2023 13:31',
        shift: '2',
        machine: 'Станок 1',
        mine: '«Есаульская»',
        location: 'Заезд на КШ 26-29',
        well: '101',
        startDepth: '0',
        endDepth: '254',
        drilled: '254',
        type: 'ВЗД'
    },
    {
        datetime: '12.03.2023 13:31',
        shift: '2',
        machine: 'Станок 1',
        mine: '«Есаульская»',
        location: 'Заезд на КШ 26-29',
        well: '101',
        startDepth: '0',
        endDepth: '254',
        drilled: '254',
        type: 'ВЗД'
    },
    {
        datetime: '12.03.2023 13:31',
        shift: '2',
        machine: 'Станок 1',
        mine: '«Есаульская»',
        location: 'Заезд на КШ 26-29',
        well: '101',
        startDepth: '0',
        endDepth: '254',
        drilled: '254',
        type: 'ВЗД'
    },
    {
        datetime: '12.03.2023 13:31',
        shift: '2',
        machine: 'Станок 1',
        mine: '«Есаульская»',
        location: 'Заезд на КШ 26-29',
        well: '101',
        startDepth: '0',
        endDepth: '254',
        drilled: '254',
        type: 'ВЗД'
    },
    {
        datetime: '12.03.2023 13:31',
        shift: '2',
        machine: 'Станок 1',
        mine: '«Есаульская»',
        location: 'Заезд на КШ 26-29',
        well: '101',
        startDepth: '0',
        endDepth: '254',
        drilled: '254',
        type: 'ВЗД'
    },
    {
        datetime: '12.03.2023 13:31',
        shift: '2',
        machine: 'Станок 1',
        mine: '«Есаульская»',
        location: 'Заезд на КШ 26-29',
        well: '101',
        startDepth: '0',
        endDepth: '254',
        drilled: '254',
        type: 'ВЗД'
    },
    {
        datetime: '12.03.2023 13:31',
        shift: '2',
        machine: 'Станок 1',
        mine: '«Есаульская»',
        location: 'Заезд на КШ 26-29',
        well: '101',
        startDepth: '0',
        endDepth: '254',
        drilled: '254',
        type: 'ВЗД'
    },
    {
        datetime: '12.03.2023 13:31',
        shift: '2',
        machine: 'Станок 1',
        mine: '«Есаульская»',
        location: 'Заезд на КШ 26-29',
        well: '101',
        startDepth: '0',
        endDepth: '254',
        drilled: '254',
        type: 'ВЗД'
    },
    {
        datetime: '12.03.2023 13:31',
        shift: '2',
        machine: 'Станок 1',
        mine: '«Есаульская»',
        location: 'Заезд на КШ 26-29',
        well: '101',
        startDepth: '0',
        endDepth: '254',
        drilled: '254',
        type: 'ВЗД'
    },
];

// --- Components ---

const StatisticsPage: FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const token = (user as any)?.access_token as string | undefined;
    const pad = (n: number) => String(n).padStart(2, '0');
    const now = new Date();
    const dayAgo = new Date(now.getTime() - 24*60*60*1000);
    const [periodFrom, setPeriodFrom] = useState<Date>(dayAgo);
    const [periodTo, setPeriodTo] = useState<Date>(now);
    const [activePicker, setActivePicker] = useState<'from' | 'to' | null>(null);

    const [mines, setMines] = useState<IdName[]>([]);
    const [locations, setLocations] = useState<IdName[]>([]);
    const [wells, setWells] = useState<IdName[]>([]);
    const [rigs, setRigs] = useState<IdName[]>([]);
    const [selectedMine, setSelectedMine] = useState<string>("");
    const [selectedLocation, setSelectedLocation] = useState<string>("");
    const [selectedWell, setSelectedWell] = useState<string>("");
    const [selectedRig, setSelectedRig] = useState<string>("");
    const [paramsLoading, setParamsLoading] = useState(false);
    const [paramsError, setParamsError] = useState<string | null>(null);

    useEffect(() => {
        let ignore = false;

        const fetchLists = async () => {
            setParamsLoading(true);
            setParamsError(null);

            try {
                const data = await fetchReportParams(token);
                if (ignore) return;

                setMines(data.main_refs?.mine_list || []);
                setLocations(data.main_refs?.deposit_list || []);
                setWells(data.well_list || []);
                setRigs(data.main_refs?.station_list || []);
            } catch (e) {
                console.error(e);
                if (!ignore) {
                    setParamsError(e instanceof Error ? e.message : 'Не удалось загрузить параметры отчетов');
                }
            } finally {
                if (!ignore) setParamsLoading(false);
            }
        };

        fetchLists();

        return () => {
            ignore = true;
        };
    }, [token]);

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.filterGroup}>
                    <select style={{...dropdownStyle, width: 180, height: 28}} value={selectedRig} onChange={e=>setSelectedRig(e.target.value)}>
                        <option value="">Все буровые</option>
                        {rigs.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                    <select style={{...dropdownStyle, width: 180, height: 28}} value={selectedMine} onChange={e=>setSelectedMine(e.target.value)}>
                        <option value="">Все шахты</option>
                        {mines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                </div>
                <div style={styles.filterGroup}>
                    <select style={{...dropdownStyle, width: 180, height: 28}} value={selectedWell} onChange={e=>setSelectedWell(e.target.value)}>
                        <option value="">Все скважины</option>
                        {wells.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                    <select style={{...dropdownStyle, width: 180, height: 28}} value={selectedLocation} onChange={e=>setSelectedLocation(e.target.value)}>
                        <option value="">Месторождение</option>
                        {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                </div>
                <div style={styles.filterGroupInlineDates}>
                    <span style={{ color:'#bbb', fontSize:14, marginLeft: 8 }}>с</span>
                    <button style={{...styles.dateButton, background: activePicker==='from' ? '#2E3749' : '#424F67', width: 200, height: 28 }} onClick={()=>setActivePicker('from')}>
                        <span style={styles.dateText}>{`${pad(periodFrom.getDate())}.${pad(periodFrom.getMonth()+1)}.${String(periodFrom.getFullYear()).slice(-2)} ${pad(periodFrom.getHours())}:${pad(periodFrom.getMinutes())}`}</span>
                        <span style={styles.dateIcon}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="2" y="3" width="12" height="11" rx="2" stroke="#888" strokeWidth="1"/>
                                <path d="M5 1V4" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
                                <path d="M11 1V4" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
                                <path d="M3 6H13" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
                            </svg>
                        </span>
                    </button>
                    <span style={{ color:'#bbb', fontSize:14 }}>по</span>
                    <button style={{...styles.dateButton, background: activePicker==='to' ? '#2E3749' : '#424F67', width: 200, height: 28 }} onClick={()=>setActivePicker('to')}>
                        <span style={styles.dateText}>{`${pad(periodTo.getDate())}.${pad(periodTo.getMonth()+1)}.${String(periodTo.getFullYear()).slice(-2)} ${pad(periodTo.getHours())}:${pad(periodTo.getMinutes())}`}</span>
                        <span style={styles.dateIcon}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="2" y="3" width="12" height="11" rx="2" stroke="#888" strokeWidth="1"/>
                                <path d="M5 1V4" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
                                <path d="M11 1V4" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
                                <path d="M3 6H13" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
                            </svg>
                        </span>
                    </button>
                </div>
                <div style={styles.actions}>
                    <button style={styles.showStatsButton} onClick={()=>navigate('/statistics/add')}>Добавить отчет</button>
                </div>
            </div>

            {paramsError && (
                <div style={styles.errorMessage}>
                    Не удалось загрузить параметры отчетов: {paramsError}
                </div>
            )}

            {paramsLoading && (
                <div style={styles.infoMessage}>Загрузка параметров отчетов...</div>
            )}

            <div style={styles.tableContainer}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>Дата/время</th>
                            <th style={styles.th}>Смена</th>
                            <th style={styles.th}>Станок</th>
                            <th style={styles.th}>Шахта</th>
                            <th style={styles.th}>Расположение</th>
                            <th style={styles.th}>Скважина</th>
                            <th style={styles.th}>Начало</th>
                            <th style={styles.th}>Конец</th>
                            <th style={styles.th}>Пробурено</th>
                            <th style={styles.th}>Тип</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mockData.map((row, index) => (
                            <tr key={index}>
                                <td style={styles.td}>{row.datetime}</td>
                                <td style={styles.td}>{row.shift}</td>
                                <td style={styles.td}>{row.machine}</td>
                                <td style={styles.td}>{row.mine}</td>
                                <td style={styles.td}>{row.location}</td>
                                <td style={styles.td}>{row.well}</td>
                                <td style={styles.td}>{row.startDepth}</td>
                                <td style={styles.td}>{row.endDepth}</td>
                                <td style={{...styles.td, color: '#32cd32'}}>{row.drilled}</td>
                                <td style={styles.td}>{row.type}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div style={styles.footer}>
                <span>Показано 1-15 из 1345</span>
                <div style={styles.pagination}>
                    <button style={styles.pageLink}>&lt;</button>
                    <button style={{...styles.pageLink, ...styles.activePage}}>1</button>
                    <button style={styles.pageLink}>2</button>
                    <button style={styles.pageLink}>3</button>
                    <button style={styles.pageLink}>...</button>
                    <button style={styles.pageLink}>90</button>
                    <button style={styles.pageLink}>&gt;</button>
                </div>
            </div>
        {activePicker && (
            <DateTimePickerModal
                isOpen={true}
                initialDate={activePicker === 'from' ? periodFrom : periodTo}
                onClose={()=>setActivePicker(null)}
                onSelect={(d)=>{
                    if (activePicker === 'from') {
                        setPeriodFrom(d);
                        if (d > periodTo) setPeriodTo(d);
                    } else {
                        if (d < periodFrom) setPeriodFrom(d);
                        setPeriodTo(d);
                    }
                    setActivePicker(null);
                }}
            />
        )}
        </div>
    );
};

// --- Styles ---

const styles: { [key: string]: CSSProperties } = {
    container: {
        
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        color: '#fff'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        marginTop: 16,
        marginBottom: 16,
        gap: 8,
        backgroundColor: '#1F2534',
        padding: 8,
        borderRadius: 4,
        border: '1px solid #2E3749',
        boxShadow: '0 2px 16px #0000003D'
    },
    filters: {
        display: 'flex',
        gap: 12,
        alignItems: 'center'
    },
    filterGroup: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingTop: 0
    },
    filterGroupInlineDates: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        paddingTop: 0
    },
    filterGroupDatesVertical: {
        display: 'flex',
        gap: 12,
        paddingTop: 16
    },
    actions: {
        display: 'flex',
        gap: 8,
        marginLeft: 'auto',
        flexWrap: 'nowrap',
        justifyContent: 'flex-end'
    },
    dropdown: dropdownStyle,
    // Vertical date picker (shared look with other pages)
    dateField: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: -20 },
    dateLabel: { fontSize: '12px', color: '#808080', paddingLeft: '2px' },
    dateButton: {
        background: '#2C2C2C',
        color: '#fff',
        border: '1px solid #2F3136',
        borderRadius: 4,
        padding: '4px 12px',
        width: '260px',
        height: '28px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer'
    },
    dateText: { fontSize: '14px', color: '#fff' },
    dateIcon: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    
    showStatsButton: {
        background: '#0066FF',
        color: '#fff',
        border: 'none',
        height: '28px',
        borderRadius: 4,
        padding: '0 16px',
        cursor: 'pointer',
        fontSize: '14px',
        minWidth: '120px'
    },
    calendarButton: {
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '20px',
        height: '20px'
    },
    tableContainer: {
        flex: 1,
        overflow: 'auto',
        background: '#1F2534',
        borderRadius: 16,
        border: '1px solid #2E3749',
        boxShadow: '0 2px 16px #0000003D'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        minWidth: '1200px'
    },
    th: {
        padding: '12px',
        textAlign: 'left',
        fontSize: 14,
        color: '#aaa',
        fontWeight: 500,
        background: '#293145',
        position: 'sticky',
        top: 0
    },
    td: {
        padding: '12px',
        fontSize: 14,
        borderBottom: '1px solid #333'
    },
    errorMessage: {
        background: '#3A1F26',
        border: '1px solid #7F2A36',
        color: '#ffb4bd',
        borderRadius: 8,
        padding: '10px 12px',
        marginBottom: 12,
        fontSize: 14
    },
    infoMessage: {
        background: '#1F2534',
        border: '1px solid #2E3749',
        color: '#cfd3dc',
        borderRadius: 8,
        padding: '10px 12px',
        marginBottom: 12,
        fontSize: 14
    },
    footer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: 16,
        fontSize: 14,
        color: '#aaa'
    },
    pagination: {
        display: 'flex',
        gap: 4
    },
    pageLink: {
        background: '#333',
        color: '#fff',
        border: '1px solid #444',
        borderRadius: 8,
        padding: '6px 12px',
        cursor: 'pointer'
    },
    activePage: {
        background: '#007bff',
        borderColor: '#007bff'
    }
};

export default StatisticsPage;
